<script setup lang="ts">
import type { Agents, AgentsAttributes } from "~/layers/agents/types/Agents";
import { useMetaData } from "~/composables/useMetaData";

import AgentsHero from "~/layers/agents/components/AgentsHero.vue";

const { findOne } = useStrapi();

const { data: agents } = await useAsyncData("agent", () =>
  findOne<Agents>("agent", {
    populate: {
      cover: "*",
      seo: {
        populate: "*",
      },
      blocks: {
        on: {
          "agents.forma-uvedomleniya": {
            populate: "*",
          },
          "shared.fos-block": {
            populate: "*",
          },
          "career.blok-banner-dokumenty": {
            populate: "*",
          },
          "shared.answer-first": {
            populate: "*",
          },
          "shared.faq": {
            populate: "*",
          },
        },
      },
    },
  }),
);

function parseProjects(nodes: Array<{ children?: Array<{ text?: string }> }>): string[] {
  return (nodes || [])
    .map((n) =>
      (n?.children || [])
        .map((c) => c?.text || "")
        .join(" ")
        .trim(),
    )
    .filter(Boolean);
}

function parseProjectsAsItems(nodes: Array<{ children?: Array<{ text?: string }> }>): { label: string }[] {
  return parseProjects(nodes).map((text) => ({ label: text }));
}

const projectItems = computed(() => parseProjectsAsItems(agents?.value?.data?.attributes?.projects));

const getAgentsPage = computed<AgentsAttributes>(() => agents.value?.data.attributes);

// AEO-контент теперь из админки (dynamic zone). Хардкод ниже — фолбэк, пока блока в CMS нет.
const cmsAnswerFirst = computed<string>(
  () => (getAgentsPage.value?.blocks ?? []).find((b: any) => b.__component === "shared.answer-first")?.text ?? "",
);
const cmsFaqItems = computed<{ q: string; a: string }[]>(() => {
  const b = (getAgentsPage.value?.blocks ?? []).find((x: any) => x.__component === "shared.faq");
  return (b?.items ?? []).map((i: any) => ({ q: i.question, a: i.answer }));
});

const answerFirstText =
  "Атлас Девелопмент приглашает к сотрудничеству агентов и агентства недвижимости Екатеринбурга. Партнёрам доступны все объекты в каталоге: квартиры, коммерческие помещения, паркинги и кладовые. Агентское вознаграждение выплачивается после регистрации сделки. Для подключения звоните: +7 (343) 364-56-59.";

const faqs = [
  {
    q: "Как стать агентом-партнёром Атлас Девелопмент?",
    a: "Для сотрудничества заполните форму на сайте или позвоните по телефону +7 (343) 364-56-59. Сотрудничество доступно как для частных лиц, так и для агентств недвижимости.",
  },
  {
    q: "Какое вознаграждение получает агент при продаже квартиры?",
    a: "Размер агентского вознаграждения уточняйте у менеджеров Атлас Девелопмент по телефону +7 (343) 364-56-59. Выплаты производятся после регистрации сделки.",
  },
  {
    q: "Нужно ли быть официально трудоустроенным для партнёрства?",
    a: "Нет, для партнёрства не требуется официальное трудоустройство. Сотрудничество возможно как для частных лиц, так и для агентств недвижимости.",
  },
  {
    q: "Какие объекты доступны агентам-партнёрам?",
    a: "Агентам доступны все актуальные объекты в каталоге: квартиры, коммерческие помещения, паркинги и кладовые в строящихся и готовых жилых комплексах Екатеринбурга.",
  },
  {
    q: "Как быстро выплачивается агентское вознаграждение?",
    a: "Агентское вознаграждение выплачивается после регистрации договора долевого участия в Росреестре. Точные сроки и условия уточняйте у менеджеров по телефону +7 (343) 364-56-59.",
  },
  {
    q: "Проводит ли Атлас Девелопмент обучение для агентов?",
    a: "Атлас Девелопмент поддерживает партнёров актуальными материалами по проектам, условиям продаж и ипотечным программам. Менеджеры компании всегда готовы ответить на вопросы.",
  },
  {
    q: "Как агенту получить прайс-листы и документы по проектам?",
    a: "Актуальные прайс-листы, планировки и документы предоставляются после регистрации в качестве партнёра. Свяжитесь с менеджерами по телефону +7 (343) 364-56-59 или через форму на сайте.",
  },
];

const components: Record<string, Component> = {
  "agents.forma-uvedomleniya": defineAsyncComponent(
    () => import("~/layers/fos/components/notification/NotificationBaseSection.vue"),
  ),
  "shared.fos-block": defineAsyncComponent(() => import("~/layers/pages/components/FosSection.vue")),
  "career.blok-banner-dokumenty": defineAsyncComponent(
    () => import("~/layers/pages/components/ExternalBannerSection.vue"),
  ),
};

const seoTitle = computed(() => getAgentsPage.value?.seo?.metaTitle || "Агентам | Атлас Девелопмент");
const seoDescription = computed(
  () =>
    getAgentsPage.value?.seo?.metaDescription ||
    "Агентам | «Атлас Девелопмент» - амбициозная команда профессионалов, успешно работающая на рынке девелопмента с 2006 года. Генеральный директор — Владимир Городенкер. В г. Екатеринбург расположена штаб-квартира компании. Проекты Группы компаний «Атлас Девелопмент» — Инновационные башни iTower, Жилой комплекс «Парк столиц», Логопарк Кольцовский.",
);

const META_DATA = computed(() => {
  return {
    og: {
      component: "Frame",
      title: seoTitle.value,
      description: seoDescription.value,
      logo: "https://atlasgroup.su/uploads/main/009/bf4c6ab88c322a476fed0d22e3ef5bf9f5782259ca962d56edea638edc678ece/logo.svg",
      url: "www.atlasgroup.su",
      bg: "linear-gradient(to bottom right, #091536, black)",
    },
    meta: {
      title: seoTitle.value,
      "theme-color": "#ffffff",
      description: seoDescription.value,
      author: "Атлас Девелопмент",
      robots: "index, follow",
      og_title: seoTitle.value,
      og_description: seoDescription.value,
      og_url: "https://atlasdevelopment.su/",
      og_type: "website",
      tw_title: seoTitle.value,
      tw_description: seoDescription.value,
      tw_url: "https://atlasdevelopment.su/",
    },
  };
});
useMetaData(META_DATA);

const { showSeoBlocks } = useSeoBlocks();

useHead({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Агентам",
        description:
          "Мы выстраиваем долгосрочные отношения с нашими партнерами и формируем вокруг среду профессионализма, доверия и комфорта.",
        url: "https://atlasdevelopment.su/agents",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Главная",
              item: "https://atlasdevelopment.su/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Агентам",
            },
          ],
        },
      }),
    },
  ],
});
</script>

<template>
  <h1 class="sr-only">Агентам недвижимости</h1>
  <AgentsHero :content="getAgentsPage" />
  <LevelsWorkingWithAgentsSection />
  <SeoAnswerFirst :text="cmsAnswerFirst || answerFirstText" />
  <component
    :is="components[block.__component]"
    v-for="(block, index) of getAgentsPage.blocks"
    :key="index"
    :content="block"
    :bg-color="block.__component === 'career.blok-banner-dokumenty' ? 'bg-white' : ''"
    :projects="block.__component === 'agents.forma-uvedomleniya' ? projectItems : []"
  />
  <SeoFaqBlock :items="cmsFaqItems.length ? cmsFaqItems : faqs" />
</template>

<style scoped></style>
